import React, { useState } from 'react';
import { AppDispatch, RootState } from 'src/store/store';
import { useDispatch, useSelector } from 'react-redux';
import { updateCellPhone } from 'src/features/notifications/utils';
import { Modal } from 'src/components/new/modal/Modal';
import { ModalTitle } from 'src/components/new/modal/ModalTitle';
import { ModalContent } from 'src/components/new/modal/ModalContent';
import { ModalFooter } from 'src/components/new/modal/ModalFooter';
import { TextField } from 'src/components/new/inputs/TextField';
import { LabeledCheckbox } from 'src/components/new/inputs/Checkbox';
import BkSnackBar from 'src/components/snackbar/BkSnackBar';
import { setCurrentUser } from 'src/features/user/userSlice';
import { refreshToken } from 'src/features/user/utils';
import { format, useMask } from '@react-input/mask';
import { countryPhoneInfo } from 'src/utils/constant';
import parsePhoneNumberFromString from 'libphonenumber-js';

interface AddCellPhoneNumberProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const AddCellPhoneNumber: React.FC<AddCellPhoneNumberProps> = ({ open, onClose, onSuccess }) => {
  const dispatch: AppDispatch = useDispatch();

  const currentUser = useSelector((state: RootState) => state.user.currentUser);

  const mask = countryPhoneInfo[currentUser.countryCode as keyof typeof countryPhoneInfo]?.mask;
  
  const [cellPhone, setCellPhone] = useState<string>(
    currentUser.cellPhoneNumber
      ? format(currentUser.cellPhoneNumber, {
          mask,
          replacement: { '_': /\d/ },
        })
      : '',
  );
  const [checkboxError, setCheckboxError] = useState(false);
  const [error, setError] = useState('');

  const [consent, setConsent] = useState(currentUser.isSmsAllowed);

  const inputRef = useMask({
    mask,
    replacement: { '_': /\d/ },
    showMask: true
  });

  const [snackBarState, setSnackbarState] = useState<{
    message: string;
    isOpened: boolean;
    severity: 'error' | 'success' | 'info' | 'warning';
  }>({
    message: '',
    isOpened: false,
    severity: 'error',
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCellPhone(e.target.value);
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setConsent(e.target.checked);
  };

  const handleSubmit = () => {
    if (!consent) {
      setCheckboxError(true);
      return;
    }

    setCheckboxError(false);

    const formattedPhone = parsePhoneNumberFromString(cellPhone, currentUser.countryCode);

    if (formattedPhone && !formattedPhone.isValid()) {
      setError('Wrong phone number format');
      return;
    }

    dispatch(
      updateCellPhone({
        phone: cellPhone.replace(/\D/g, ''),
        isSmsAllowed: consent,
        countryCode: currentUser.countryCode,
      }),
    ).then((result) => {
      if (updateCellPhone.fulfilled.match(result)) {
        setSnackbarState({
          ...snackBarState,
          isOpened: true,
          message: 'Successfully added phone number!',
          severity: 'success',
        });
        dispatch(setCurrentUser({ user: { ...currentUser, isSmsAllowed: consent, cellPhoneNumber: cellPhone } }));
        dispatch(refreshToken());
        onSuccess();
        onClose();
      }
      if (updateCellPhone.rejected.match(result)) {
        setSnackbarState({
          ...snackBarState,
          isOpened: true,
          message: 'Something went wrong',
          severity: 'error',
        });
        onClose();
      }
    });
  };

  return (
    <>
      <Modal open={open} onClose={onClose} sx={{ minWidth: '27.5rem' }}>
        <ModalTitle small onClose={onClose}>
          Add Phone Number
        </ModalTitle>
        <ModalContent small>
          <TextField
            required
            value={cellPhone}
            name="cellPhone"
            id="cellPhone"
            onChange={handleChange}
            placeholder={mask}
            inputRef={inputRef}
            error={error}
            label="Cell Phone"
          />
          <LabeledCheckbox
            error={checkboxError ? 'You must consent to proceed.' : undefined}
            checked={consent}
            onChange={handleCheckboxChange}
            label="I consent to receive SMS messages from Certificate of Service. Message and data rates may apply."
          />
        </ModalContent>
        <ModalFooter onSubmit={handleSubmit} submitText="Save" />
      </Modal>
      <BkSnackBar
        open={snackBarState.isOpened}
        handleCloseSnackbar={() => setSnackbarState({ ...snackBarState, isOpened: false })}
        snackMessage={snackBarState.message}
        severity={snackBarState.severity}
      />
    </>
  );
};

export default AddCellPhoneNumber;
